import { useMemo, useState } from 'react';
import { color, mono, severityBand, type SeverityBand } from '../tokens';
import { type Theme, type SevInputs } from '../fixtures';
import { useSynth, useSynthNav } from '../SynthesisContext';
import { asText, cleanLabel } from '../asText';
import { countOf } from '../../../utils/pluralize';

const DIMENSION_LABELS: Array<{ key: keyof SevInputs; label: string; hint: string }> = [
  { key: 'customer', label: 'Customer impact',       hint: 'How much the end customer feels this, 1–5.' },
  { key: 'effort',   label: 'Effort to work around', hint: 'Field/employee time lost per occurrence, 1–5.' },
  { key: 'freq',     label: 'Occurrence frequency',  hint: 'How often in a normal week, 1–5.' },
  { key: 'blast',    label: 'Blast radius',          hint: 'Roles / regions / teams affected, 1–5.' },
];

function BandChip({ band }: { band: SeverityBand }) {
  const c = severityBand[band] || severityBand.MED;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.4,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    }}>{band}</span>
  );
}

// Five tiny bars filled to n/5 for one severity dimension.
function DimensionBars({ n, fg }: { n: number; fg: string }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{
          width: 14, height: 6, borderRadius: 2,
          background: i <= n ? fg : color.hairline,
        }} />
      ))}
    </div>
  );
}

const TRIAGE = [
  { key: 'INVESTIGATE', label: 'Investigate', bg: '#DBEAFE', fg: '#1D4ED8', border: '#93C5FD', tooltip: 'Root cause not yet clear; needs deeper analysis before action.' },
  { key: 'REMEDIATE', label: 'Remediate', bg: '#DCFCE7', fg: '#16A34A', border: '#86EFAC', tooltip: 'Clear fix exists and should be prioritized.' },
  { key: 'REDESIGN', label: 'Redesign', bg: '#F3E8FF', fg: '#7C3AED', border: '#C4B5FD', tooltip: 'Structural change is needed; patching will not hold.' },
  { key: 'ACCEPT', label: 'Accept', bg: '#F1F5F9', fg: '#64748B', border: '#CBD5E1', tooltip: 'Known trade-off; monitor and revisit if conditions change.' },
  { key: 'ESCALATE', label: 'Escalate', bg: '#FEF3C7', fg: '#B45309', border: '#FCD34D', tooltip: 'Needs leadership decision beyond current engagement scope.' },
] as const;

function validWho(v: string): boolean {
  const s = asText(v).trim();
  if (!s) return false;
  return !/^(unknown|n\/a|na|null|undefined|-|—)$/i.test(s);
}

function ExpandedPanel({
  theme,
  themeQuotes,
  onOpenEvidence,
}: {
  theme: Theme;
  themeQuotes: Array<{ text: string; who: string }>;
  onOpenEvidence: () => void;
}) {
  const { engagement } = useSynth();
  const INTERVIEWS = engagement.interviews;
  const c = severityBand[theme.sev] || severityBand.MED;
  const mentionedBy = theme.mentionedBy?.filter((n) => validWho(n));
  const derivedMentionedBy = Array.from(new Set(themeQuotes.map((q) => asText(q.who).trim()).filter(validWho)));
  const names = mentionedBy && mentionedBy.length ? mentionedBy : derivedMentionedBy;
  return (
    <div style={{
      background: color.surfaceAlt, borderTop: `1px solid ${color.hairline}`,
      padding: '18px 18px 20px 60px',
    }}>
      {asText(theme.summary)
        ? <p style={{ margin: 0, fontSize: 13, color: color.text, maxWidth: 640, lineHeight: 1.55 }}>{asText(theme.summary)}</p>
        : <p style={{ margin: 0, fontSize: 13, color: color.textMuted }}>—</p>}

      {/* Severity breakdown card */}
      <div style={{
        marginTop: 16, background: color.surface, border: `1px solid ${color.hairline}`,
        borderRadius: 12, padding: '14px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>
            SEVERITY BREAKDOWN
          </span>
          <span style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: c.fg }}>
            {theme.sevScore.toFixed(1)}
          </span>
          <BandChip band={theme.sev} />
          <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: mono, color: color.textFaint }}>
            0.35·cust + 0.15·effort + 0.25·freq + 0.25·blast
          </span>
        </div>

        <div style={{
          marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
        }}>
          {DIMENSION_LABELS.map((d) => (
            <div key={d.key} title={d.hint}>
              <div style={{ fontSize: 11.5, color: color.textMuted, marginBottom: 6 }}>{d.label}</div>
              <DimensionBars n={theme.sevInputs[d.key]} fg={c.fg} />
              <div style={{ marginTop: 6, fontSize: 11, fontFamily: mono, color: color.textMuted }}>
                {theme.sevInputs[d.key]}/5
              </div>
              {theme.sevRationale && (
                <div style={{ marginTop: 6, fontSize: 11, color: color.textFaint, lineHeight: 1.45 }}>
                  {asText(theme.sevRationale[d.key])}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Systems / sub-themes / evidence */}
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 8 }}>
            SYSTEMS INVOLVED
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {theme.systems.length > 0 ? theme.systems.map((s, i) => (
              <span key={i} style={{
                fontSize: 11.5, fontFamily: mono, padding: '3px 8px', borderRadius: 6,
                background: '#F1F5F9', color: color.text,
              }}>{asText(s)}</span>
            )) : <span style={{ fontSize: 12, color: color.textFaint }}>—</span>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 8 }}>
            SUB-THEMES
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {theme.subthemes.length > 0 ? theme.subthemes.map((s, i) => (
              <span key={i} style={{
                fontSize: 11.5, padding: '3px 8px', borderRadius: 999,
                background: color.pillActiveBg, color: color.purpleDeep, border: `1px solid ${color.chipBorder}`,
              }}>{asText(s)}</span>
            )) : <span style={{ fontSize: 12, color: color.textFaint }}>—</span>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 8 }}>
            EVIDENCE
          </div>
          <span onClick={onOpenEvidence} style={{ fontSize: 12.5, color: color.purple, cursor: 'pointer' }}>
            {theme.quotes} verbatim quotes
          </span>
          <div style={{ fontSize: 12, color: color.textMuted, marginTop: 4 }}>
            {theme.mentions} of {countOf(INTERVIEWS, 'source')}
          </div>
          {themeQuotes.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {themeQuotes.slice(0, 6).map((q, i) => (
                <div key={`${q.text}-${i}`} style={{ fontSize: 11.5, color: color.textMuted, lineHeight: 1.45 }}>
                  "{asText(q.text)}"{validWho(q.who) ? ` - ${asText(q.who)}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 8 }}>
          MENTIONED BY
        </div>
        {names.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {names.map((name) => (
              <span key={name} style={{
                fontSize: 11.5, padding: '3px 8px', borderRadius: 999,
                background: color.pillMutedBg, color: color.text, border: `1px solid ${color.hairline}`,
              }}>
                {asText(name)}
              </span>
            ))}
          </div>
        ) : theme.mentions > 0 ? (
          <div style={{ fontSize: 12, color: color.textMuted }}>
            Stakeholder names available when mentionedBy[] is added to the theme schema
          </div>
        ) : (
          <div style={{ fontSize: 12, color: color.textFaint }}>—</div>
        )}
      </div>
    </div>
  );
}

function ThemeRow({
  theme,
  open,
  onToggle,
  onOpenEvidence,
}: {
  theme: Theme;
  open: boolean;
  onToggle: () => void;
  onOpenEvidence: () => void;
}) {
  const { engagement } = useSynth();
  const { quotes } = useSynth();
  const INTERVIEWS = engagement.interviews;
  const c = severityBand[theme.sev] || severityBand.MED;
  const themeQuotes = useMemo(
    () => quotes.filter((q) => q.themeIdx === theme.rank).map((q) => ({ text: asText(q.text), who: asText(q.who) })),
    [quotes, theme.rank],
  );
  return (
    <div style={{
      background: color.surface, border: `1px solid ${color.hairline}`, borderRadius: 12,
      marginBottom: 10, overflow: 'hidden',
    }}>
      <div
        role="button"
        onClick={onToggle}
        style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 16,
          alignItems: 'center', padding: '14px 18px', cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 13, fontFamily: mono, color: color.purple, fontWeight: 600 }}>
          {String(theme.rank).padStart(2, '0')}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: color.ink, whiteSpace: 'normal' }}>{cleanLabel(theme.name)}</div>
          <div style={{ marginTop: 8, height: 6, borderRadius: 999, background: '#F1F5F9', maxWidth: 520 }}>
            <div style={{ width: `${theme.percent}%`, height: '100%', borderRadius: 999, background: color.purpleGrad }} />
          </div>
        </div>
        <span style={{ fontSize: 13, fontFamily: mono, color: color.textMuted }}>
          {theme.mentions}/{INTERVIEWS}
        </span>
        <span style={{ fontSize: 13, fontFamily: mono, color: color.textMuted }}>
          {theme.percent}%
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'end' }}>
          <span style={{ fontSize: 13, fontFamily: mono, fontWeight: 700, color: c.fg }}>
            {theme.sevScore.toFixed(1)}<span style={{ color: color.textFaint, fontWeight: 400 }}>/5</span>
          </span>
          <BandChip band={theme.sev} />
        </span>
      </div>
      {open && <ExpandedPanel theme={theme} themeQuotes={themeQuotes} onOpenEvidence={onOpenEvidence} />}
    </div>
  );
}

// Distribution + average are DERIVED here for display only — off the same sevScore
// the assembler computed. The component never re-derives a theme's own score.
const BAND_ORDER: SeverityBand[] = ['HIGH', 'MED-HIGH', 'MED', 'LOW'];

export default function KeyThemes() {
  const { themes: fixtureThemes, engagement } = useSynth();
  const { expandedThemeRank, setExpandedThemeRank, setQuoteFilter, setActive } = useSynthNav();
  const INTERVIEWS = engagement.interviews;
  const [localOpenRank, setLocalOpenRank] = useState<number | null>(null);
  const openRank = expandedThemeRank ?? localOpenRank;

  const avgSeverity =
    Math.round((fixtureThemes.reduce((a, t) => a + t.sevScore, 0) / fixtureThemes.length) * 10) / 10;
  const distribution = BAND_ORDER
    .map((band) => ({ band, count: fixtureThemes.filter((t) => t.sev === band).length }))
    .filter((d) => d.count > 0);
  const distTotal = distribution.reduce((a, d) => a + d.count, 0) || 1;

  return (
    <div>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>KEY THEMES</span>
      </div>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
        Ranked themes across {countOf(INTERVIEWS, 'interview')}
      </h2>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
        Frequency bar · severity score · click a row to expand per-theme drilldown including how severity was scored.
      </p>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, padding: '10px 14px',
        background: color.surfaceAlt, border: `1px solid ${color.hairline}`, borderRadius: 10,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, alignSelf: 'center', marginRight: 4 }}>
          TRIAGE TAGS
        </span>
        {TRIAGE.map((t) => (
          <span key={t.key} title={t.tooltip} style={{
            fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
            background: t.bg, color: t.fg, border: `1px solid ${t.border}`, cursor: 'help',
          }}>
            {t.label}
          </span>
        ))}
      </div>

      {/* Summary bar */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 24, alignItems: 'start',
        border: `1px solid ${color.hairline}`, borderRadius: 12, padding: '16px 18px', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>AVG SEVERITY</div>
          <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, fontFamily: mono, color: color.ink }}>
            {avgSeverity.toFixed(1)}<span style={{ fontSize: 13, color: color.textFaint, fontWeight: 400 }}>/5</span>
          </div>
        </div>
        <div style={{ borderLeft: `1px solid ${color.hairline}`, paddingLeft: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>SEVERITY DISTRIBUTION</div>
          <div style={{ marginTop: 8, height: 10, borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
            {distribution.map((d) => (
              <span key={d.band} style={{ width: `${(d.count / distTotal) * 100}%`, background: severityBand[d.band].fg }} />
            ))}
          </div>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {distribution.map((d) => (
              <span key={d.band} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: color.textMuted }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: severityBand[d.band].fg }} />
                {d.band} · {d.count}
              </span>
            ))}
          </div>
        </div>
        <div style={{ borderLeft: `1px solid ${color.hairline}`, paddingLeft: 24, maxWidth: 240 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>HOW IT'S SCORED</div>
          <p style={{ margin: '6px 0 0', fontSize: 11.5, color: color.methodology, lineHeight: 1.5 }}>
            Weighted avg of customer impact · effort to work around · frequency · blast radius. Each 1–5. The
            platform computes the score — the model only emits the four inputs.
          </p>
        </div>
      </div>

      {/* Column header */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 16,
        padding: '0 18px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.textFaint,
      }}>
        <span>#</span>
        <span>THEME · FREQUENCY</span>
        <span>MENTIONS</span>
        <span>COVERAGE</span>
        <span style={{ justifySelf: 'end' }}>SEVERITY</span>
      </div>

      {/* Rows */}
      {fixtureThemes.map((t) => (
        <ThemeRow
          key={t.rank}
          theme={t}
          open={openRank === t.rank}
          onToggle={() => {
            const next = openRank === t.rank ? null : t.rank;
            setLocalOpenRank(next);
            setExpandedThemeRank(next);
          }}
          onOpenEvidence={() => {
            setQuoteFilter(t.rank);
            setActive('quotes');
          }}
        />
      ))}
    </div>
  );
}
